import type { Channel, ChannelFolder } from '../../types/channel';

// The channel column is one flat list of rows: root channels first, then for each folder its header followed by its
// channels (unless collapsed). Dragging reorders that flat list; these helpers translate it back into "which folder, and
// before which channel" and apply a move to the grouped view so the drop doesn't flash back while the server catches up.

const HEADER_PREFIX = '::folder::';

export const folderRowId = (folderId: string) => `${HEADER_PREFIX}${folderId}`;
const isFolderRow = (id: string) => id.startsWith(HEADER_PREFIX);

export type Grouped = { roots: Channel[]; byFolder: Map<string, Channel[]> };

/** The ids of every visible row, top to bottom. */
export function flatRowIds(grouped: Grouped, folders: Pick<ChannelFolder, 'id'>[], collapsed: Record<string, boolean>): string[] {
  const ids = grouped.roots.map((c) => c.id);
  for (const f of folders) {
    ids.push(folderRowId(f.id));
    if (!collapsed[f.id]) for (const c of grouped.byFolder.get(f.id) || []) ids.push(c.id);
  }
  return ids;
}

export type ChannelMove = { channelId: string; folderId: string | null; beforeId: string | null };

/**
 * Where a dragged channel ended up. A channel belongs to the nearest folder header above it (none: the root list), so
 * dropping just below a header puts it at the top of that folder and dropping above the first header ends the root list.
 * `beforeId` is the channel right below it in the same group, or null for the end of the group.
 */
export function channelMoveFromOrder(order: readonly string[], movedId: string): ChannelMove {
  const i = order.indexOf(movedId);
  let folderId: string | null = null;
  for (let k = i - 1; k >= 0; k--) {
    if (isFolderRow(order[k])) { folderId = order[k].slice(HEADER_PREFIX.length); break; }
  }
  const next = order[i + 1];
  return { channelId: movedId, folderId, beforeId: next && !isFolderRow(next) ? next : null };
}

/** Folder order after dragging one folder: the folder id that now follows it, or null if it is last. */
export function folderBeforeId(order: readonly string[], movedId: string): string | null {
  return order[order.indexOf(movedId) + 1] ?? null;
}

/** Applies a channel move to the grouped view (a copy; the input is untouched). */
export function applyChannelMove(grouped: Grouped, move: ChannelMove): Grouped {
  let moving: Channel | undefined;
  const take = (list: Channel[]) => list.filter((c) => { if (c.id === move.channelId) { moving = c; return false; } return true; });
  const roots = take(grouped.roots);
  const byFolder = new Map<string, Channel[]>();
  grouped.byFolder.forEach((list, fid) => byFolder.set(fid, take(list)));
  if (!moving) return grouped;
  const placed: Channel = { ...moving, folderId: move.folderId };
  const target = move.folderId ? (byFolder.get(move.folderId) || []) : roots;
  const at = move.beforeId ? target.findIndex((c) => c.id === move.beforeId) : -1;
  if (at >= 0) target.splice(at, 0, placed); else target.push(placed);
  if (move.folderId) byFolder.set(move.folderId, target);
  return { roots, byFolder };
}
